import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { gsap } from "gsap";

// Styled Components
const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  text-align: center;
  background-color: #000;
  color: #f2f2f2;
`;

const Nav = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 5px;
    padding: 0 10px;
  }
`;

const Pill = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  background-color: ${(props) => (props.$active ? props.$dynamicColor : "#333")};
  color: ${(props) => (props.$active ? "#fff" : "#f2f2f2")};
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
  transition: background-color 0.3s ease, color 0.3s ease;

  &:hover {
    background-color: #f2f2f2;
    color: #262626;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  padding: 10px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #0D0D0D;
  
  border-radius: 12px;
  padding: 20px;
  text-align: left;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.4);
  }
`;

const Image = styled.img`
  width: 100%;
  border-radius: 12px;
  margin-bottom: 15px;
`;

const Title = styled.h3`
  font-size: 18px;
  margin: 0 0 10px;
  color: #f2f2f2;
`;

const Description = styled.p`
  font-size: 14px;
  color: #d3d3d3;
  margin-bottom: 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 10px;
  margin-bottom: 20px;
`;

const InfoKey = styled.div`
font-size: 14px;
  color: #d3d3d3;
`;

const InfoValue = styled.div`
font-size: 14px;
  color: #d3d3d3;
`;

const LiveLinkButton = styled.a`
  display: inline-block;
  padding: 10px 20px;
  font-size: 14px;
  color: #f2f2f2;
  background: #444;
  border-radius: 30px;
  text-decoration: none;
  text-align: center;
  font-weight: bold;
  transition: background 0.3s ease, color 0.3s ease;

  &:hover {
    background: #f2f2f2;
    color: #262626;
  }

  &::after {
    content: " →";
  }
    @media (max-width: 768px) {
    width: 100%; /* Full width on mobile */
    padding: 10px 0; /* Adjust padding for centered content */
    border-radius: 20px; /* Slightly less rounded on full width */
  }
`;

const NavRow = styled.div`
  display: flex;
  flex-wrap: wrap; /* Allows wrapping for mobile */
  align-items: center; /* Ensures vertical alignment of text and pills */
  justify-content: center; /* Centers the row content horizontally */
  gap: 10px;
  width: 100%; /* Full width for consistent alignment */
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column; /* Stacks content vertically on mobile */
    align-items: center; /* Centers everything on mobile */
  }
`;

const NavLabel = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #f2f2f2;
  white-space: nowrap; /* Prevents wrapping */
  margin-right: 5px; /* Adds space between label and pills */

  @media (max-width: 768px) {
    margin-right: 0; /* No spacing when stacked */
    margin-bottom: 10px; /* Add spacing between the label and pills */
  }
`;




const ProjectsGrid = ({ projects, dynamicColor }) => {
  const [activeCategory, setActiveCategory] = useState("All");
  
  const getButtonText = (action) => {
    switch (action) {
      case "read":
        return "Read here";
      case "listen":
        return "Listen here";
      case "live":
      default:
        return "See it live";
    }
  };
  useEffect(() => {
    const activePill = document.querySelector(".active-pill");
    if (activePill) {
      gsap.to(activePill, {
        backgroundColor: dynamicColor,
        duration: 0.5,
        ease: "power2.out",
      });
    }
  }, [dynamicColor]);

  const categories = [
    "All",
    ...Array.from(new Set(projects.flatMap((project) => project.categories))),
  ];

  const filteredProjects = projects.filter(
    (project) =>
      project.categories.includes(activeCategory) || activeCategory === "All"
  );

  const handleCategoryChange = (category) => {
    // Reset all pills
    document.querySelectorAll(".active-pill").forEach((pill) => {
      pill.classList.remove("active-pill");
      pill.style.backgroundColor = "#333";
      pill.style.color = "#f2f2f2";
    });

    // Set new active pill
    const newActivePill = document.querySelector(`[data-category="${category}"]`);
    if (newActivePill) {
      newActivePill.classList.add("active-pill");
      gsap.to(newActivePill, {
        backgroundColor: dynamicColor,
        color: "#fff",
        duration: 0.5,
      });
    }

    setActiveCategory(category);
    gsap.to(".project-card", {
      opacity: 0,
      scale: 0.9,
      duration: 0.3,
      onComplete: () => {
        gsap.to(".project-card", {
          opacity: 1,
          scale: 1,
          stagger: 0.1,
          duration: 0.5,
        });
      },
    });
  };

  return (
    <Wrapper>
       <NavRow>
       <NavLabel>Selected Projects:</NavLabel>
      <Nav>
        {categories.map((category) => (
          <Pill
            key={category}
            className={activeCategory === category ? "active-pill" : ""}
            $active={activeCategory === category}
            $dynamicColor={dynamicColor}
            data-category={category}
            onClick={() => handleCategoryChange(category)}
          >
            {category}
          </Pill>
        ))}
      </Nav>
      </NavRow>
      <Grid>
        {filteredProjects.map((project, index) => (
          <Card key={index} className="project-card">
            <Image src={project.image} alt={project.title} />
            <Title>{project.title}</Title>
            <Description>{project.description}</Description>
            <InfoGrid>
              {project.year && (
                <>
                  <InfoKey>Year</InfoKey>
                  <InfoValue>{project.year}</InfoValue>
                </>
              )}
              {project.role && (
                <>
                  <InfoKey>Role</InfoKey>
                  <InfoValue>{project.role}</InfoValue>
                </>
              )}
              {project.client && (
                <>
                  <InfoKey>Brand</InfoKey>
                  <InfoValue>{project.client}</InfoValue>
                </>
              )}
              {project.skills && (
                <>
                  <InfoKey>Skills</InfoKey>
                  <InfoValue>{project.skills}</InfoValue>
                </>
              )}
              {project.tools && (
                <>
                  <InfoKey>Tools</InfoKey>
                  <InfoValue>{project.tools}</InfoValue>
                </>
              )}
            </InfoGrid>
            {project.liveLink && (
              <LiveLinkButton
                href={project.liveLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {getButtonText(project.buttonAction)}
              </LiveLinkButton>
            )}
          </Card>
        ))}
      </Grid>
    </Wrapper>
  );
  
};

export default ProjectsGrid;
